import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useTemplatePagePartials from '@hooks/useTemplatePagePartials'
import { generateUrls } from '@hooks/useTemplatePagePartial'

import BlankState from '@components/BlankState'
import PageFilterModule from '@components/PageFilterModule'

import BlankStateImage from '../../../../assets/no-template-page-partial.svg'

import TemplatePagePartial from '../TemplatePagePartial'

import type { PageContextPayload } from '../../index'

const buildPageFilterFields = (isBrandUser: boolean) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID or String',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        disabled: isBrandUser,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: '100',
        defaultSelectValue: 100,
        inputComponent: 'select',
        style,
      },
      key: 'limit',
      label: 'Limit',
      values: [
        { key: 150, label: '150' },
        { key: 200, label: '200' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { currentUser, isBrandUser } = useCurrentUser()
  const scopedPageFilters = isBrandUser ? { ...pageFilters, brand_id: currentUser?.brand_id } : pageFilters

  const { hidden }:{ hidden?: 'true' | 'false' } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden ?? 'false'

  const templatePagePartialsPayload = useTemplatePagePartials({
    filters: {
      ...scopedPageFilters,
      hidden: isHidden === 'true',
    },
    performHttpRequests: true,
    requestOptions: {
      include_template_page_partial_brand: true,
      include_template_page_partial_preview: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePagePartials,
    filteredTemplatePagePartialsCount,
    hasFilteredTemplatePagePartials,
    loading,
  } = templatePagePartialsPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateTemplatePagePartialModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { templatePagePartialsIndexUrl, archivedTemplatePagePartialsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Template Partials | Template Manager</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateTemplatePagePartialModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: templatePagePartialsIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current',
          },
          {
            href: archivedTemplatePagePartialsIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Template Partials"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTemplatePagePartials && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Template Partials">
                <BlankState
                  imageUrl={BlankStateImage}
                  subtitle="Try adjusting your filters or create new template page partial"
                  title="No Template Page Partials"
                />
              </DashboardModule>
            )}

            {!loading && hasFilteredTemplatePagePartials && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Template Partials">
                <Box flexDirection="column">
                  {filteredTemplatePagePartials.map(templatePagePartial => (
                    <TemplatePagePartial
                      key={templatePagePartial.id}
                      // @ts-expect-error TS2322
                      templatePagePartial={templatePagePartial}
                    />
                  ))}

                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagePartialsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={isBrandUser ? { brand_id: currentUser?.brand_id } : undefined}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(isBrandUser)}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
